import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button, Select } from '@gravity-ui/uikit';
import { ManagerAuditAction } from '@odonix/contracts';
import { useManagersApi } from '../../lib/api/managers-context.js';
import { Page, Section } from '../../ui/page.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';
import { AuditFeed } from './audit-feed.js';
import { MANAGER_ACTION_LABEL } from './labels.js';
import { DateInput } from '../../ui/date-input.js';

/**
 * Действия менеджеров: входы, открытые карточки, звонки и письма — и всё, что
 * сотрудник платформы сделал с самими менеджерами: процент, премии,
 * закрепление клиник. Отбор — менеджер, действие и период — живёт в адресе.
 *
 * Период — дни по Москве: «за вчера» сотрудник платформы понимает как
 * московские сутки, а не UTC.
 */

const routeApi = getRouteApi('/shell/managers/audit');
const PAGE_SIZE = 50;
const ANY = '__any';

function moscowMidnight(day: string): string {
  return new Date(`${day}T00:00:00+03:00`).toISOString();
}

function nextDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

export function ManagerAuditScreen() {
  const api = useManagersApi();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const managers = useQuery({ queryKey: ['managers', 'with-deleted'], queryFn: () => api.list({ includeDeleted: true }) });

  const query = useInfiniteQuery({
    queryKey: ['manager-audit', search.managerId, search.action, search.from, search.to],
    queryFn: ({ pageParam }) =>
      api.audit({
        limit: PAGE_SIZE,
        cursor: pageParam,
        ...(search.managerId === undefined ? {} : { managerId: search.managerId }),
        ...(search.action === undefined ? {} : { action: search.action }),
        ...(search.from === undefined ? {} : { from: moscowMidnight(search.from) }),
        ...(search.to === undefined ? {} : { to: moscowMidnight(nextDay(search.to)) }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const entries = query.data?.pages.flatMap((page) => page.entries) ?? [];
  const filtered =
    search.managerId !== undefined || search.action !== undefined || search.from !== undefined || search.to !== undefined;

  return (
    <Page
      title="Действия менеджеров"
      description="Входы, открытые карточки, звонки и письма — и что сотрудники платформы сделали с самими менеджерами."
    >
      <div className="flex flex-wrap items-end gap-3">
        <Select
          size="m"
          width={240}
          label="Менеджер:"
          filterable={(managers.data?.length ?? 0) > 8}
          value={[search.managerId ?? ANY]}
          options={[
            { value: ANY, content: 'Все' },
            ...(managers.data ?? []).map((manager) => ({
              value: manager.id,
              content: manager.status === 'deleted' ? `${manager.fullName} (удалён)` : manager.fullName,
            })),
          ]}
          onUpdate={(values) => {
            const value = values[0];
            void navigate({ search: (prev) => ({ ...prev, managerId: value === ANY ? undefined : value }) });
          }}
        />
        <Select
          size="m"
          width={250}
          label="Действие:"
          value={[search.action ?? ANY]}
          options={[
            { value: ANY, content: 'Любое' },
            ...ManagerAuditAction.options.map((action) => ({ value: action, content: MANAGER_ACTION_LABEL[action] })),
          ]}
          onUpdate={(values) => {
            const value = values[0];
            void navigate({
              search: (prev) => ({ ...prev, action: value === ANY ? undefined : ManagerAuditAction.parse(value) }),
            });
          }}
        />
        <div className="w-40">
          <DateInput
            label="С"
            value={search.from ?? ''}
            onUpdate={(value) => {
              void navigate({ search: (prev) => ({ ...prev, from: value === '' ? undefined : value }) });
            }}
          />
        </div>
        <div className="w-40">
          <DateInput
            label="По"
            value={search.to ?? ''}
            onUpdate={(value) => {
              void navigate({ search: (prev) => ({ ...prev, to: value === '' ? undefined : value }) });
            }}
          />
        </div>
        {filtered ? (
          <Button
            view="flat-secondary"
            size="m"
            onClick={() => {
              void navigate({ search: {} });
            }}
          >
            Сбросить
          </Button>
        ) : null}
      </div>

      {query.isError ? (
        <ErrorNote title="Журнал не загрузился" error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <LoadingRows rows={8} height={48} />
      ) : entries.length === 0 ? (
        <Empty title={filtered ? 'По этому отбору записей нет' : 'Менеджеры пока ничего не делали'} />
      ) : (
        <>
          <Section flush>
            <AuditFeed entries={entries} />
          </Section>
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button view="outlined" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                Показать ещё
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Page>
  );
}
